import React, {useEffect, useState, useCallback} from 'react';
import ABTest, {DebugMenu} from 'react-controlled-ab';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';
import {useUserState} from 'contexts/user';
import {Container, Col, Row, Button} from 'reactstrap';
import classNames from 'classnames';
import PageMeta from 'components/PageMeta';
import Header from 'components/Elements/Header';
import Section, {SectionImage} from 'components/Elements/Section';
import Webinar from 'components/Webinar';
import Video from 'components/Video';
import SalesCopy from 'components/SalesCopy';
import Footer from 'components/Elements/Footer';
import ShareButtons from 'components/Social/ShareButtons';
import RegistrationModal from 'components/RegistrationModal';
import ModalExitScreening from 'components/Modals/ModalExitScreening';
import {MasterclassCta247} from 'components/SalesCta';
import track from 'utils/track';
import useBalanceText from 'utils/useBalanceText';

const COMPLETED_MASTERCLASS_EVENT = 'Completed Watching HHC Masterclass';

export default function ScreeningIndex({replayOverride = false, ...props}) {
  const {bucket, events = []} = useUserState();
  const [anchorScrolled, setAnchorScrolled] = useState(false);
  const hasCompletedMasterclass = events.includes(COMPLETED_MASTERCLASS_EVENT);

  useEffect(() => {
    const {HS_BEACON_ID} = process.env;
    if (!HS_BEACON_ID || !window.Beacon) return;
    window.Beacon('init', HS_BEACON_ID);
    return () => {
      window.Beacon('destroy');
    };
  }, []);

  useEffect(() => {
    if (hasCompletedMasterclass)
      track('Product Viewed', {
        product_id: 'C-HHC',
        sku: 'C-HHC',
        name: 'Healthy Heart',
        variant: 'hhc-masterclass',
        value: 247,
        currency: 'usd'
      });
  }, [hasCompletedMasterclass]);

  useEffect(() => {
    if (anchorScrolled) return;
    if (events.includes(COMPLETED_MASTERCLASS_EVENT)) {
      setAnchorScrolled(true);
      if (window !== undefined && window.location.hash === '#join') {
        document.getElementById('watch-cta-thankyou').scrollIntoView();
      }
    }
  }, [events, anchorScrolled]);

  const webinarHeading = (
    <Row>
      <Col>
        <h4 className="section-heading text-center mb-1">Healthy Heart Masterclass</h4>
        <h4 className="text-center mt-0 mb-1">
          <em>4 Heart Myths & 10 Groundbreaking Truths</em>
        </h4>
        <h5 className="text-400 text-center mt-0 mb-4">with Dr. Mimi Guarneri & Ocean Robbins</h5>
      </Col>
    </Row>
  );

  const webinarFooter = (
    <Row>
      <Col>
        <h4 className="text-center text-600 mb-0">
          <a
            href="https://cdn.foodrevolution.org/hhc/Healthy%20Heart%20Masterclass%20Workbook.pdf"
            className="workbook-download text-underline"
            target="_blank"
          >
            Download your FREE workbook here.
          </a>
        </h4>
        <p className="balance-text text-center mt-4">
          {
            {
              prevent: (
                <>
                  IMPORTANT: Taking the time to keep your heart healthy is the most valuable thing you can do today.
                  You’ll be so glad you did!
                </>
              ),
              optimize: (
                <>
                  IMPORTANT: Taking the time to get the truth about what makes your heart strong is the most valuable
                  thing you can do today. You’ll be so glad you did!
                </>
              ),
              reverse: (
                <>
                  IMPORTANT: Taking the time to keep your heart healthy is the most valuable thing you can do today.
                  You’ll be so glad you did!
                </>
              )
            }[bucket]
          }
        </p>
      </Col>
    </Row>
  );

  useBalanceText();
  return (
    <div className="page d-flex flex-column">
      <PageMeta title="Watch the Healthy Heart Masterclass with Dr. Mimi Guarneri" />

      <div className="page-content">
        <Header className="background-black header-wrap" logoWhite style="dark" />

        <Section
          id="watch-header"
          color="white"
          className="section-angle-first"
          innerClass={classNames(`section-webinar-inner${replayOverride ? '-replay' : null}`)}
          angle="none"
        >
          <Container>
            {replayOverride ? (
              <>
                {webinarHeading}
                <Row className="d-flex justify-content-center">
                  <Col lg="10" className="video-col px-0 px-sm-3">
                    <Video
                      url="https://customer-ros8xhsmbkksflna.cloudflarestream.com/6a14bf8fd8c14fa78de753d4dc513848/watch"
                      label="HHC - Masterclass"
                    />
                  </Col>
                </Row>
                {webinarFooter}
              </>
            ) : (
              <Webinar
                noun="Masterclass"
                headingSection={webinarHeading}
                footerSection={webinarFooter}
                label="HHC - Masterclass"
              />
            )}
          </Container>
        </Section>

        {events.includes(COMPLETED_MASTERCLASS_EVENT) && !replayOverride ? (
          <>
            <Section id="watch-cta-thankyou" color="gradient-indigo-purple-right" innerClass="" angle="both">
              <Container>
                <Row className="d-flex justify-content-center text-white text-center">
                  <Col lg="10">
                    <div className="background-green p-4 rounded mb-4 border-dashed border-white border-3">
                      <p className="balance-text m-0">
                        <strong>One-time offer available now:</strong> Please do not close or refresh the page.
                      </p>
                    </div>
                    <h2 className="section-heading mt-0">
                      Today only: enroll in the groundbreaking <em>Healthy Heart Course</em> for 50% off
                    </h2>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <MasterclassCta247 />
                  </Col>
                </Row>
              </Container>
            </Section>

            <Section id="" color="black">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-only-heading text-center text-white">
                      {
                        {
                          prevent: <>Do You Know How To Stop Heart Disease From Stopping You?</>,
                          optimize: <>Do You Know How To Have The Strongest Heart Possible?</>,
                          reverse: <>Do You Know How To Stop Heart Disease From Stopping You?</>
                        }[bucket]
                      }
                    </h2>
                  </Col>
                </Row>
              </Container>
            </Section>

            <SalesCopy version="screening">
              <>
                <Row className="text-white text-center">
                  <Col>
                    <h5 className="section-heading text-400 mb-5">
                      <strong>One-time offer:</strong> Get a special 50% off discount & free Heart Health Collection
                      with extra interviews and recipes (a $97 bonus gift). Please do not close or refresh this page.
                    </h5>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <MasterclassCta247 />
                  </Col>
                </Row>
              </>
            </SalesCopy>
          </>
        ) : null}
      </div>

      {replayOverride ? (
        <Section
          id="replay-cta"
          color="gradient-indigo-purple-right"
          innerClass="text-center text-white section-webinar-inner-cta"
          angle="none"
        >
          <Container>
            <Row>
              <Col>
                <h4 className="section-heading">
                  Are you interested in enrolling in Dr. Mimi Guarneri’s <em>Healthy Heart</em> course?
                </h4>
                <h5>Click below to learn more:</h5>
                <Button className="btn-join btn-lg" color="cta" href="/join/" target="_blank">
                  Join Now!
                </Button>
              </Col>
            </Row>
          </Container>
        </Section>
      ) : (
        <>
          <RegistrationModal />
          <ModalExitScreening />
        </>
      )}

      <div className="footer-content mt-auto">
        <Footer />
        <ShareButtons floating />
      </div>
    </div>
  );
}
